// Added by Sejune Oh on 2019-09-30
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { ElementTimeStampProps } from '@/helper/define'
import { getQueryPath } from '@/helper/util'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import Rootstore from '../../..'
import {
  reqGetStudyCustomColumns,
  reqGetStudyCustomColumn,
  reqCreateStudyCustomColumn,
  reqUpdateStudyCustomColumn,
  reqDeleteStudyCustomColumn,
  reqGetPredefinedCustomColumnsSchema,
  reqGetStudyResultValueType,
} from './CustomColumnSchemaStore.service'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export interface CustomColumnElementProps extends ElementTimeStampProps {
  id?: string
  key?: string
  display_name?: string
  type?: CustomColumnEnumTypes
  possible_values?: string
  user_defined?: boolean
  formats?: NumberFormat[]
}

export interface DisplableCustomColumnElementProps extends CustomColumnElementProps {
  user_defined_str?: string
  possible_values_str?: string
  min_value?: string | number
  max_value?: string | number
  delete_btn?: JSX.Element
  index?: number
  isUpdated?: boolean
}

export interface NumberFormat {
  /** background color */
  bg_color: string
  /** font color */
  fg_color: string
  format: string
  min: string
  max: string
  /** Full width */
  fullwidth: boolean
}

export const customColumnTypes = ['string', 'bool', 'enum', 'int', 'real', 'external_enum']
export type CustomColumnEnumTypes = 'int' | 'real' | 'string' | 'bool' | 'enum' | 'external_enum'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class CustomColumnSchema {
  root?: Rootstore
  _totalCount: number = 0
  _elements: Array<CustomColumnElementProps | DisplableCustomColumnElementProps> = []
  _cachedElements: Array<DisplableCustomColumnElementProps> = []

  constructor(rootStore: Rootstore) {
    this.root = rootStore
    makeAutoObservable(this)
  }

  get totalCount() {
    return this._totalCount
  }

  get elements() {
    return this._elements
  }

  get cachedElements() {
    return this._cachedElements
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setTotalCount = (count: number) => {
    this._totalCount = count
  }

  setElements = (elements: Array<CustomColumnElementProps | DisplableCustomColumnElementProps>) => {
    this._elements = elements
  }

  cacheElements = (elements: Array<CustomColumnElementProps | DisplableCustomColumnElementProps>) => {
    this._cachedElements = elements
  }

  clearCachedElements = () => {
    this._cachedElements = []
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getElements = async (proxyToken: string, path: string) => {
    const {
      data: { total_count, items },
    } = await reqGetStudyCustomColumns(proxyToken, path)
    this._setTotalCount(parseInt(total_count!))
    return items! as DisplableCustomColumnElementProps[]
  }

  getElement = async (proxyToken: string, id: string) => {
    const { data: rtnElement } = await reqGetStudyCustomColumn(proxyToken, id)
    return rtnElement
  }

  addElement = async (proxyToken: string, element: CustomColumnElementProps | DisplableCustomColumnElementProps) => {
    const { data: rtnElement } = await reqCreateStudyCustomColumn(proxyToken, element)
    return rtnElement
  }

  updateElement = async (
    proxyToken: string,
    id: string,
    element: CustomColumnElementProps | DisplableCustomColumnElementProps
  ) => {
    const { data: updatedElement } = await reqUpdateStudyCustomColumn(proxyToken, id, element)
    return updatedElement
  }

  removeElement = async (proxyToken: string, id: string) => {
    return await reqDeleteStudyCustomColumn(proxyToken, id)
  }

  checkDuplicatedKey = async (proxyToken: string, key: string) => {
    const filter = { $op: 'and', key }
    const path = getQueryPath({ pageIndex: 0, filter: JSON.stringify(filter) })

    const elements = await this.getElements(proxyToken, path)
    return elements.length > 0
  }

  // TODO: fix this
  getPredefinedCustomColumns = async () => {
    const {
      data: { items },
    } = await reqGetPredefinedCustomColumnsSchema()
    return items as DisplableCustomColumnElementProps[]
  }

  checkExistedCustomColumnInfoByKey = async (proxyToken: string, key: string) => {
    const { data } = await reqGetStudyResultValueType(proxyToken, key)
    return data
  }
}
